// components/tables/tablesShared.ts
'use client';
import type { KernelHandle, UserContext } from '#types/types';
import type { RefObject } from 'react';
import type { KernelStoreApi } from '@/kernel/kernelStores';
import { canEditTable, useTablesStore } from './tablesStore';
import { canAccessAnalyst } from '#lib/access/analystAccess';

const scopeOf = (user: UserContext): string => `${user.dbUserId}:${user.organisationId}:${user.isImpersonating}`;
const messageOf = (error: unknown): string => {
  const message = error instanceof Error ? error.message : '';
  return message && message.length < 500 && !/prisma|invocation|stack trace/i.test(message) ? message : 'Shared data could not be saved or loaded. Retry to reconnect.';
};
let loadSequence = 0;

export function subscribeSharedTables(kernelStore: KernelStoreApi, kernel: RefObject<KernelHandle | null>): () => void {
  useTablesStore.getState().resetShared(null);
  const beforeUnload = (event: BeforeUnloadEvent) => {
    const s = useTablesStore.getState();
    if (Object.entries(s.shared).some(([id, meta]) => s.docs[id]?.sheets !== meta.savedSheets)) { event.preventDefault(); event.returnValue = ''; }
  };
  window.addEventListener('beforeunload', beforeUnload);
  const refreshing = new Set<string>();
  const unsubscribe = kernelStore.subscribe((state, previous) => {
  const user = state.userContext;
  if (scopeOf(user) !== scopeOf(previous.userContext) || canAccessAnalyst(user) !== canAccessAnalyst(previous.userContext)) {
    loadSequence += 1;
    useTablesStore.getState().resetShared(null);
    return;
  }
  if (state.inventory.Property.registry === previous.inventory.Property.registry) return;
  const store = useTablesStore.getState();
  if (store.sharedScope !== scopeOf(user)) return;
  for (const property of Object.values(state.inventory.Property.registry)) {
    if (!property.analystWorkbook || !store.docs[property.analystWorkbook.id]) continue;
    const before = previous.inventory.Property.registry[property.id];
    if (property === before) continue;
    if (property.analystRevision !== store.shared[property.analystWorkbook.id]?.revision && property.analystWorkbook === before?.analystWorkbook) {
      if (refreshing.has(property.id)) continue;
      refreshing.add(property.id);
      void kernel.current?.syncEntity('Property', property.id, { force: true, requireFresh: true }).catch(() => {
        useTablesStore.setState({ sharedError: 'Shared data changed, but refresh failed. Reload to reconnect.' });
      }).finally(() => refreshing.delete(property.id));
    } else store.hydrateShared(property, user);
  }
  });
  return () => { unsubscribe(); window.removeEventListener('beforeunload', beforeUnload); loadSequence += 1; useTablesStore.getState().resetShared(null); };
}

export async function loadSharedTables(kernel: KernelHandle): Promise<void> {
  const user = kernel.getUserContext();
  const scope = scopeOf(user);
  if (useTablesStore.getState().sharedScope !== scope) useTablesStore.getState().resetShared(scope);
  const sequence = ++loadSequence;
  useTablesStore.setState({ sharedLoading: true, sharedError: null });
  try {
    if (!user.dbUserId || !canAccessAnalyst(user)) throw new Error('Analyst access is not enabled for this account.');
    const result = await kernel.queryInventory('Property', { limit: 400, force: true });
    if (result.error) throw new Error(result.error);
    for (const id of result.ids) {
      if (sequence !== loadSequence || scopeOf(kernel.getUserContext()) !== scope) return;
      let property = await kernel.syncEntity('Property', id, { force: true, requireFresh: true });
      if (sequence !== loadSequence || scopeOf(kernel.getUserContext()) !== scope) return;
      if (!property) throw new Error('Shared asset not found or access denied.');
      if (!property?.analystWorkbook) {
        await kernel.mutateInventory([{ entity: 'Property', action: 'UPDATE', id, payload: { analystInitialize: true } }], 'Initialize the asset’s canonical shared rent roll');
        property = await kernel.syncEntity('Property', id, { force: true, requireFresh: true });
      }
      if (sequence !== loadSequence || scopeOf(kernel.getUserContext()) !== scope) return;
      if (!property?.analystWorkbook) throw new Error('The shared workbook could not be loaded. Retry to reconnect.');
      useTablesStore.getState().hydrateShared(property, user);
    }
    if (sequence === loadSequence) {
      const allowed = new Set(result.ids);
      useTablesStore.setState((s) => ({
        docs: Object.fromEntries(Object.entries(s.docs).filter(([, d]) => !d.propertyId || allowed.has(d.propertyId))),
        order: s.order.filter((id) => !s.docs[id]?.propertyId || allowed.has(s.docs[id]!.propertyId!)),
      }));
    }
  } catch (error) {
    if (sequence === loadSequence) useTablesStore.setState({ sharedError: messageOf(error) });
    throw new Error(messageOf(error));
  } finally {
    if (sequence === loadSequence) useTablesStore.setState({ sharedLoading: false });
  }
}

export async function saveSharedTable(kernel: KernelHandle, docId: string): Promise<void> {
  const state = useTablesStore.getState();
  const doc = state.docs[docId];
  if (!doc?.propertyId) return;
  const meta = state.shared[docId];
  if (!canEditTable(doc)) throw new Error('Only this asset’s assigned owners can edit its shared workbook.');
  if (!meta || meta.saving) throw new Error('A shared save is already in progress.');
  if (meta.savedSheets === doc.sheets) return;
  const scope = state.sharedScope;
  const setStatus = (patch: Partial<typeof meta>) => useTablesStore.setState((s) =>
    s.sharedScope === scope ? { shared: { ...s.shared, [docId]: { ...s.shared[docId]!, ...patch } } } : {});
  setStatus({ saving: true, error: null });
  try {
    await kernel.mutateInventory([{ entity: 'Property', action: 'UPDATE', id: doc.propertyId, payload: {
      analystWorkbook: { id: doc.id, propertyId: doc.propertyId, sheets: doc.sheets }, analystRevision: meta.revision,
    } }], 'Save the asset’s shared Analyst workbook');
    if (useTablesStore.getState().sharedScope !== scope) return;
    setStatus({ revision: meta.revision + 1, savedSheets: doc.sheets, saving: false });
    const property = await kernel.syncEntity('Property', doc.propertyId, { force: true, requireFresh: true });
    if (useTablesStore.getState().sharedScope !== scope) return;
    if (!property?.analystWorkbook) throw new Error('Saved, but could not refresh the shared version. Reload to verify.');
    useTablesStore.getState().hydrateShared(property, kernel.getUserContext());
  } catch (error) {
    setStatus({ saving: false, error: messageOf(error) });
    throw new Error(messageOf(error));
  }
}

export async function reloadSharedTable(kernel: KernelHandle, docId: string): Promise<void> {
  const { docs, sharedScope } = useTablesStore.getState();
  const propertyId = docs[docId]?.propertyId;
  const sheetsBefore = docs[docId]?.sheets;
  if (!propertyId) return;
  const property = await kernel.syncEntity('Property', propertyId, { force: true, requireFresh: true });
  if (sharedScope !== useTablesStore.getState().sharedScope) return;
  const currentSheets = useTablesStore.getState().docs[docId]?.sheets;
  if (currentSheets !== sheetsBefore && currentSheets !== property?.analystWorkbook?.sheets) throw new Error('New edits were made while reloading. Your draft has been kept; retry when ready.');
  if (!property?.analystWorkbook) throw new Error('Unable to reload. Your draft has been kept.');
  useTablesStore.getState().hydrateShared(property, kernel.getUserContext(), true);
}
